import { type FC, useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import { Dropdown, type IDropdownProps } from 'shared/ui'
import { useSendFromContext } from 'widgets/SendFrom/context/context'
import { ButtonTrigger } from 'widgets/SendFrom/ui/ButtonTrigger/ButtonTrigger'
import {
  type ITextFiledTriggerProps,
  TextFiledTrigger,
} from 'widgets/SendFrom/ui/TextFiledTrigger/TextFiledTrigger'
import styles from './styles.module.scss'

export type ISendFromUiProps = {
  width?: number
  onClearError?: () => void
  triggerVariant: 'button' | 'textField'
  textFiledTriggerProps?: ITextFiledTriggerProps
  contentOffset?: [number, number]
  showActiveIcon?: boolean
} & IDropdownProps

export const SendFromUi: FC<ISendFromUiProps> = observer(
  ({
    width = 338,
    textFiledTriggerProps,
    onClearError,
    triggerVariant,
    contentOffset = [0, 0],
    showActiveIcon = false,
    ...props
  }) => {
    const {
      setError,
      selectedDropdownItem,
      dropdownItems,
      setSelectedDropdownItem,
      init,
      loadMore,
      loadingMore,
      onSearch,
      search,
      loading,
    } = useSendFromContext()

    const items = dropdownItems.map((item) => {
      const inboxMatches = item.id === selectedDropdownItem?.data?.inbox?.id

      const getActiveValue = () => {
        if (
          showActiveIcon &&
          inboxMatches &&
          !selectedDropdownItem?.data?.smart_option_label &&
          !selectedDropdownItem?.data?.mergeField?.name
        ) {
          return true
        }

        if (inboxMatches) {
          return (
            selectedDropdownItem?.data?.smart_option_label ||
            selectedDropdownItem?.data?.number?.formatted_number
          )
        }
        if (item.id === selectedDropdownItem?.data?.integration?.id) {
          return selectedDropdownItem?.data?.mergeField?.name
        }
      }

      return {
        ...item,
        activeValue: getActiveValue(),
        children: item.children?.map((child) => ({
          ...child,
          activeValue: child.id === selectedDropdownItem?.id,
        })),
      }
    })

    useEffect(() => {
      init()
    }, [])

    return (
      <Dropdown
        className={styles.dropdown}
        width={width}
        ariaLabel={'SendFromDropDown'}
        onToggle={() => {
          setError(false)
          onClearError?.()
        }}
        triggerComponent={(open) =>
          triggerVariant === 'button' ? (
            <ButtonTrigger active={open} />
          ) : (
            <TextFiledTrigger {...textFiledTriggerProps} />
          )
        }
        placement={'bottom-start'}
        items={items}
        onChange={setSelectedDropdownItem}
        withSearch={true}
        margin={0}
        PopperProps={{
          modifiers: [
            triggerVariant === 'button'
              ? {
                  name: 'offset',
                  options: {
                    offset: [0, 4],
                  },
                }
              : {
                  name: 'offset',
                  options: {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    offset: ({ reference }) => {
                      const [dx = 0, dy = 0] = contentOffset
                      return [-4 + dx, -reference.height - 4 + dy]
                    },
                  },
                },
          ],
        }}
        {...props}
        propsDropdownList={{
          maxHeight: 300,
          allHideOnScroll: true,
          withGrouping: triggerVariant === 'textField',
          dropdownGroupProps: {
            isScroll: false,
          },
          onLoadMore: loadMore,
          loadingMore: loadingMore,
          loading: loading,
          onSearch: onSearch,
          search: search,
        }}
      />
    )
  }
)
